/**
 * AI CONTEXT
 * Purpose: main standout feedback workspace in evaluation modal (select tags, generate text, persist summary).
 * Uses: useStandoutMoments, GPT streaming, category tab state, analytics.
 * Writes: standout moments and evaluation.standoutSummary per category.
 * An eval story is stamped with the most recent session the student attended.
 * The eval write-up still saves when that session does not exist.
 * Used by: EvaluationFormModal standout tab.
 * Refactor: split generation orchestration and persistence side effects into hooks.
 */
import { MessageOutlined, ThunderboltOutlined } from "@ant-design/icons"
import { App, Button, Input, Popover, Select, Tabs, Typography } from "antd"
import dayjs from "dayjs"
import { orderBy } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import { GptHandler } from "#classes/GptHandler"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  StandoutCategory,
  StandoutMomentConstructor,
  StandoutSummary,
  TagPool,
} from "#customTypes/composedTypes/standoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import useAnalytics from "#hooks/useAnalytics"
import { camelToWords } from "#utils/textFormatters"

import KeywordPicker from "./KeywordPicker"
import { noAttendedSessionMessage } from "./nearestAttendedSession"
import { useNearestAttendedSession } from "./useNearestAttendedSession"
import {
  OPPOSITE_COUNT_KEY,
  SortMode,
  useStandoutMoments,
} from "./useStandoutMoments"

const { TextArea } = Input
const { Text } = Typography

const MIN_TO_GENERATE = 3

function NotesPopover({
  moments,
  tagsById,
}: {
  moments: StandoutMomentConstructor[]
  tagsById: Record<Uuid, { text: string }>
}) {
  if (moments.length === 0) return null

  return (
    <Popover
      title={`Teacher Notes (${moments.length})`}
      content={
        <Flex
          col
          gap={8}
          style={{ maxHeight: 300, overflowY: "auto", maxWidth: 350 }}
        >
          {moments.map((m) => {
            const tagTexts = (m.tagIds ?? [])
              .map((id) => tagsById[id]?.text)
              .filter(Boolean)
              .join(", ")

            return (
              <Flex
                key={m.id}
                col
                gap={2}
                style={{
                  paddingBottom: 8,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {dayjs(m.createdAt).format("MMM D, YYYY")}
                  {tagTexts && ` · ${tagTexts}`}
                </Text>
                <Text style={{ fontSize: 13 }}>{m.note}</Text>
              </Flex>
            )
          })}
        </Flex>
      }
      trigger="click"
      placement="rightTop"
    >
      <Button
        type="text"
        size="small"
        icon={<MessageOutlined />}
        style={{ fontSize: 12, padding: "0 4px" }}
      >
        {moments.length} note{moments.length !== 1 ? "s" : ""}
      </Button>
    </Popover>
  )
}

const PERSONA_OPTIONS = [
  { value: "sorcerer", label: "🧙 Sorcerer" },
  { value: "jungleExplorer", label: "🌿 Jungle Explorer" },
  { value: "superheroSidekick", label: "🦸 Superhero Sidekick" },
  { value: "friendlyDinosaur", label: "🦕 Friendly Dinosaur" },
  { value: "videoGameGuide", label: "🎮 Video Game Guide" },
  { value: "secretAgent", label: "🕵️ Secret Agent" },
]

const WRITING_STYLE_OPTIONS = [
  { value: "auto", label: "Auto (random)" },
  { value: "conversational", label: "Conversational" },
  { value: "storytelling", label: "Storytelling" },
  { value: "letterStyle", label: "Letter Style" },
  { value: "observational", label: "Observational" },
  { value: "coachStyle", label: "Coach Style" },
  { value: "poeticMinimal", label: "Poetic Minimal" },
  { value: "enthusiastic", label: "Enthusiastic" },
  { value: "reflective", label: "Reflective" },
]

const ALL_STYLE_KEYS = WRITING_STYLE_OPTIONS.filter(
  (o) => o.value !== "auto"
).map((o) => o.value)

function pickUniqueStyles(count: number): string[] {
  const shuffled = [...ALL_STYLE_KEYS].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, count)
}

const CATEGORY_CONFIG: {
  key: StandoutCategory
  label: string
  pool: TagPool
  formField: "didWell" | "needsWork" | "secretMessage"
}[] = [
  { key: "thumbsUp", label: "Thumbs Up", pool: "skill", formField: "didWell" },
  {
    key: "needsWork",
    label: "Needs Work",
    pool: "skill",
    formField: "needsWork",
  },
  {
    key: "secret",
    label: "Secret Message",
    pool: "secret",
    formField: "secretMessage",
  },
]

interface StandoutMomentsPanelProps {
  studentId: Uuid
  studentFirstName: string
  semester: string
  curriculumId: Uuid
  age: number
  classGroup: string
  evaluationId?: Uuid
  classId?: Uuid
  checkedTraits?: string[]
  uncheckedTraits?: string[]
  standoutSummary?: StandoutSummary
  onFeedbackGenerated: (feedback: {
    didWell?: string
    needsWork?: string
    secretMessage?: string
  }) => void
}

type CategoryState = {
  selectedTagIds: Set<Uuid>
  sortMode: SortMode
  generatedText: string
  isGenerating: boolean
}

const defaultCategoryState = (): CategoryState => ({
  selectedTagIds: new Set(),
  sortMode: "frequency",
  generatedText: "",
  isGenerating: false,
})

export default function StandoutMomentsPanel({
  studentId,
  studentFirstName,
  semester,
  curriculumId,
  age,
  classGroup,
  evaluationId,
  classId,
  checkedTraits,
  uncheckedTraits,
  standoutSummary,
  onFeedbackGenerated,
}: StandoutMomentsPanelProps) {
  const theme = useThemeContext()
  const { message } = App.useApp()
  const { currentUserId, effectiveUserId } = useUserContext()
  const { trackStandoutAction, ANALYTICS_EVENTS } = useAnalytics()
  const abortRef = useRef<Record<string, boolean>>({})
  const hasInitialized = useRef(false)
  const [persona, setPersona] = useState("sorcerer")
  const [writingStyle, setWritingStyle] = useState("auto")

  const {
    isFulfilled,
    tagsById,
    tagStats,
    getSortedTags,
    getAutoSelectedTagIds,
    createCustomTag,
    saveStandoutMoment,
    getMostRecentMomentsByCategory,
    momentCountsByCategory,
    getRecentMomentContext,
    studentMoments,
  } = useStandoutMoments(studentId, semester, curriculumId)

  const {
    ready: attendedReady,
    sessionId: attendedSessionId,
    whenReady: whenAttendedReady,
  } = useNearestAttendedSession(studentId)

  const [categoryStates, setCategoryStates] = useState<
    Record<StandoutCategory, CategoryState>
  >({
    thumbsUp: defaultCategoryState(),
    needsWork: defaultCategoryState(),
    secret: defaultCategoryState(),
  })

  // Seed state: standoutSummary > auto-select from counts > recent moments
  // Wait for isFulfilled (tag suggestions loaded or timeout) before initializing.
  useEffect(() => {
    if (hasInitialized.current || !isFulfilled) return

    hasInitialized.current = true

    const recentMoments = getMostRecentMomentsByCategory()
    const hasAnySummary =
      standoutSummary && Object.keys(standoutSummary).length > 0
    const hasAnyMoments = Object.keys(recentMoments).length > 0

    const autoSelections: Partial<Record<StandoutCategory, Uuid[]>> = {}
    for (const config of CATEGORY_CONFIG) {
      const auto = getAutoSelectedTagIds(config.pool, config.key)
      if (auto.length > 0) autoSelections[config.key] = auto
    }
    const hasAutoSelections = Object.keys(autoSelections).length > 0

    if (!hasAnySummary && !hasAutoSelections && !hasAnyMoments) return

    setCategoryStates((prev) => {
      const next = { ...prev }
      for (const config of CATEGORY_CONFIG) {
        const cat = config.key
        const summaryEntry = standoutSummary?.[cat]
        const autoIds = autoSelections[cat]
        const momentEntry = recentMoments[cat]

        if (summaryEntry?.tagIds?.length) {
          next[cat] = {
            ...prev[cat],
            selectedTagIds: new Set(summaryEntry.tagIds),
            generatedText: summaryEntry.generatedText || "",
          }
        } else if (autoIds?.length) {
          next[cat] = {
            ...prev[cat],
            selectedTagIds: new Set(autoIds),
          }
        } else if (momentEntry?.tagIds?.length) {
          const oppositeKey = OPPOSITE_COUNT_KEY[cat]
          const filteredTagIds = oppositeKey
            ? momentEntry.tagIds.filter((id) => {
                const stats = tagStats[id]
                if (!stats) return true
                const countKey =
                  cat === "thumbsUp" ? "thumbsUpCount" : "needsWorkCount"
                return (
                  (stats[oppositeKey] as number) <= (stats[countKey] as number)
                )
              })
            : momentEntry.tagIds

          if (filteredTagIds.length > 0) {
            next[cat] = {
              ...prev[cat],
              selectedTagIds: new Set(filteredTagIds),
              generatedText: momentEntry.generatedText || "",
            }
          }
        }
      }
      return next
    })
  }, [
    isFulfilled,
    standoutSummary,
    getMostRecentMomentsByCategory,
    getAutoSelectedTagIds,
    tagStats,
  ])

  const updateCategoryState = useCallback(
    (category: StandoutCategory, patch: Partial<CategoryState>) => {
      setCategoryStates((prev) => ({
        ...prev,
        [category]: { ...prev[category], ...patch },
      }))
    },
    []
  )

  const handleToggleTag = useCallback(
    (category: StandoutCategory, tagId: Uuid) => {
      setCategoryStates((prev) => {
        const current = prev[category]
        const next = new Set(current.selectedTagIds)
        if (next.has(tagId)) {
          next.delete(tagId)
        } else {
          next.add(tagId)
        }
        return { ...prev, [category]: { ...current, selectedTagIds: next } }
      })
    },
    []
  )

  const handleRemoveTag = useCallback(
    (category: StandoutCategory, tagId: Uuid) => {
      setCategoryStates((prev) => {
        const current = prev[category]
        const next = new Set(current.selectedTagIds)
        next.delete(tagId)
        return { ...prev, [category]: { ...current, selectedTagIds: next } }
      })
    },
    []
  )

  const persistSummaryToEval = useCallback(
    (category: StandoutCategory, tagIds: Uuid[], generatedText: string) => {
      if (!evaluationId || !currentUserId) return

      const now = new Date().toISOString()
      const updatedEntry = {
        tagIds,
        generatedText,
        finalizedBy: currentUserId,
        finalizedAt: now,
      }

      void Firebase.getInstance().updateDocument(
        DOC_TYPES.EVALUATIONS,
        evaluationId,
        { [`standoutSummary.${category}`]: updatedEntry }
      )
    },
    [evaluationId, currentUserId]
  )

  // Auto-persist tag selections so deselections survive across sessions
  useEffect(() => {
    if (!hasInitialized.current || !evaluationId || !currentUserId) return

    const timer = setTimeout(() => {
      for (const config of CATEGORY_CONFIG) {
        const state = categoryStates[config.key]
        const tagIds = Array.from(state.selectedTagIds)
        persistSummaryToEval(config.key, tagIds, state.generatedText)
      }
    }, 1500)

    return () => clearTimeout(timer)
  }, [categoryStates, evaluationId, currentUserId, persistSummaryToEval])

  const handleGenerate = useCallback(
    async (category: StandoutCategory, styleOverride?: string) => {
      const state = categoryStates[category]
      if (state.selectedTagIds.size < MIN_TO_GENERATE) return

      const tagTexts = Array.from(state.selectedTagIds)
        .map((id) => tagsById[id]?.text)
        .filter(Boolean) as string[]

      const recentMoments = getRecentMomentContext(category)

      updateCategoryState(category, { isGenerating: true, generatedText: "" })
      abortRef.current[category] = false

      let accumulated = ""

      const resolvedStyle =
        styleOverride ??
        (writingStyle === "auto" ? pickUniqueStyles(1)[0] : writingStyle)

      try {
        await GptHandler.streamTagFeedback(
          category,
          tagTexts,
          {
            age,
            classGroup: camelToWords(classGroup),
            recentMoments,
            persona,
            writingStyle: resolvedStyle,
            studentFirstName,
            traits:
              category === "thumbsUp"
                ? checkedTraits
                : category === "needsWork"
                  ? uncheckedTraits
                  : undefined,
          },
          {
            onDelta: (content) => {
              if (abortRef.current[category]) return
              accumulated += content
              updateCategoryState(category, { generatedText: accumulated })
            },
            onDone: () => {
              const selectedArr = Array.from(state.selectedTagIds)

              updateCategoryState(category, {
                isGenerating: false,
              })

              const config = CATEGORY_CONFIG.find((c) => c.key === category)
              if (config && accumulated) {
                onFeedbackGenerated({ [config.formField]: accumulated })
              }

              void whenAttendedReady().then((stamp) => {
                if (!stamp.ready || !stamp.sessionId) {
                  if (stamp.ready) {
                    void message.warning(
                      noAttendedSessionMessage(studentFirstName)
                    )
                  }
                  return
                }
                void saveStandoutMoment(category, selectedArr, {
                  generatedText: accumulated,
                  evaluationId,
                  classId,
                  classSessionId: stamp.sessionId,
                })
              })

              persistSummaryToEval(category, selectedArr, accumulated)

              trackStandoutAction(
                ANALYTICS_EVENTS.STANDOUT_FEEDBACK_GENERATED,
                {
                  teacherId: effectiveUserId,
                  studentId,
                  category,
                  tagCount: tagTexts.length,
                  tagTexts,
                  evaluationId,
                  classId,
                  persona,
                  writingStyle: resolvedStyle,
                }
              )
            },
            onError: (error) => {
              updateCategoryState(category, { isGenerating: false })
              void message.error(`Generation failed: ${error}`)
            },
          }
        )
      } catch {
        updateCategoryState(category, { isGenerating: false })
        void message.error("Generation failed. Please try again.")
      }
    },
    [
      categoryStates,
      tagsById,
      getRecentMomentContext,
      updateCategoryState,
      age,
      classGroup,
      persona,
      writingStyle,
      checkedTraits,
      uncheckedTraits,
      onFeedbackGenerated,
      saveStandoutMoment,
      persistSummaryToEval,
      evaluationId,
      classId,
      whenAttendedReady,
      message,
      studentFirstName,
      trackStandoutAction,
      ANALYTICS_EVENTS,
      effectiveUserId,
      studentId,
    ]
  )

  const handleGenerateAll = useCallback(async () => {
    const eligibleCategories = CATEGORY_CONFIG.filter(
      (c) => categoryStates[c.key].selectedTagIds.size >= MIN_TO_GENERATE
    )

    const uniqueStyles =
      writingStyle === "auto"
        ? pickUniqueStyles(eligibleCategories.length)
        : ([] as string[])

    for (let i = 0; i < eligibleCategories.length; i++) {
      const style = uniqueStyles[i]
      await handleGenerate(eligibleCategories[i].key, style)
    }
  }, [categoryStates, handleGenerate, writingStyle])

  const canGenerateAll = useMemo(() => {
    return CATEGORY_CONFIG.some(
      (c) => categoryStates[c.key].selectedTagIds.size >= MIN_TO_GENERATE
    )
  }, [categoryStates])

  const isAnyGenerating = useMemo(() => {
    return CATEGORY_CONFIG.some((c) => categoryStates[c.key].isGenerating)
  }, [categoryStates])

  const tabItems = useMemo(
    () =>
      CATEGORY_CONFIG.map((config) => {
        const state = categoryStates[config.key]
        const sorted = getSortedTags(config.pool, state.sortMode, config.key)
        const canGenerate = state.selectedTagIds.size >= MIN_TO_GENERATE

        const eventCount = momentCountsByCategory[config.key] ?? 0

        const notesForCategory = orderBy(
          studentMoments.filter((m) => m.category === config.key && m.note),
          "createdAt",
          "desc"
        )

        const selectedCount = state.selectedTagIds.size
        return {
          key: config.key,
          label:
            selectedCount > 0
              ? `${config.label} (${selectedCount})`
              : config.label,
          forceRender: true,
          children: (
            <Flex col gap={12}>
              <Flex gap={8} style={{ alignItems: "center" }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {eventCount} grading event{eventCount !== 1 ? "s" : ""}
                </Text>
                <NotesPopover moments={notesForCategory} tagsById={tagsById} />
              </Flex>
              <KeywordPicker
                pool={config.pool}
                sortedTags={sorted}
                selectedTagIds={state.selectedTagIds}
                sortMode={state.sortMode}
                minRequired={MIN_TO_GENERATE}
                onSortModeChange={(mode) =>
                  updateCategoryState(config.key, { sortMode: mode })
                }
                onToggleTag={(tagId) => handleToggleTag(config.key, tagId)}
                onRemoveTag={(tagId) => handleRemoveTag(config.key, tagId)}
                onCreateCustomTag={createCustomTag}
                tagsById={tagsById}
              />

              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={() => handleGenerate(config.key)}
                disabled={!canGenerate}
                loading={state.isGenerating}
                style={{
                  alignSelf: "flex-start",
                  backgroundColor: canGenerate ? theme.eric.blue : undefined,
                }}
              >
                Generate {config.label}
              </Button>

              {state.generatedText && (
                <Flex col gap={4}>
                  <Text strong style={{ fontSize: 12 }}>
                    Generated Feedback:
                  </Text>
                  <TextArea
                    value={state.generatedText}
                    onChange={(e) =>
                      updateCategoryState(config.key, {
                        generatedText: e.target.value,
                      })
                    }
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    style={{ fontSize: 13 }}
                  />
                  <Button
                    size="small"
                    type="link"
                    onClick={() => {
                      const field = config.formField
                      onFeedbackGenerated({ [field]: state.generatedText })
                      void message.success(`${config.label} applied to form`)
                      trackStandoutAction(
                        ANALYTICS_EVENTS.STANDOUT_FEEDBACK_REAPPLIED,
                        {
                          teacherId: effectiveUserId,
                          studentId,
                          category: config.key,
                          tagCount: state.selectedTagIds.size,
                          evaluationId,
                        }
                      )
                    }}
                  >
                    Re-apply to form
                  </Button>
                </Flex>
              )}
            </Flex>
          ),
        }
      }),
    [
      categoryStates,
      getSortedTags,
      updateCategoryState,
      handleToggleTag,
      handleRemoveTag,
      createCustomTag,
      tagsById,
      handleGenerate,
      theme.eric.blue,
      momentCountsByCategory,
      studentMoments,
      onFeedbackGenerated,
      message,
      trackStandoutAction,
      ANALYTICS_EVENTS,
      effectiveUserId,
      studentId,
      evaluationId,
    ]
  )

  return (
    <Flex col gap={8} style={{ padding: "0 16px" }}>
      <Flex gap={16} style={{ alignItems: "center", flexWrap: "wrap" }}>
        <Flex gap={8} style={{ alignItems: "center" }}>
          <Text style={{ fontSize: 12, whiteSpace: "nowrap" }}>Persona:</Text>
          <Select
            value={persona}
            onChange={setPersona}
            options={PERSONA_OPTIONS}
            size="small"
            style={{ minWidth: 190 }}
          />
        </Flex>
        <Flex gap={8} style={{ alignItems: "center" }}>
          <Text style={{ fontSize: 12, whiteSpace: "nowrap" }}>Style:</Text>
          <Select
            value={writingStyle}
            onChange={setWritingStyle}
            options={WRITING_STYLE_OPTIONS}
            size="small"
            style={{ minWidth: 160 }}
          />
        </Flex>
      </Flex>

      {attendedReady && !attendedSessionId ? (
        <Text
          type="danger"
          data-testid="standout-no-attended-session"
          style={{ fontSize: 12 }}
        >
          {noAttendedSessionMessage(studentFirstName)}
        </Text>
      ) : null}

      <Tabs items={tabItems} size="small" destroyInactiveTabPane={false} />

      <Button
        type="primary"
        icon={<ThunderboltOutlined />}
        onClick={handleGenerateAll}
        disabled={!canGenerateAll}
        loading={isAnyGenerating}
        block
      >
        Generate All Categories
      </Button>
    </Flex>
  )
}
